import { useState } from 'react';
import type {
  FiscalPeriodDto,
  PeriodCheckItem,
  PeriodCloseChecksResponse,
} from '@hesabat/contracts';
import { Money } from '@hesabat/finance-core';
import { api, ProblemError } from './api';
import { formatMoney } from './money';

interface Props {
  companyId: string;
  periods: readonly FiscalPeriodDto[];
  canManage: boolean;
  /** إعادة الفتح وقيد إقفال السنة للمالك وحده — المحاسب يُقفل الفترة فقط. */
  canReopen: boolean;
  onChanged: () => void;
}

const STATUS_LABELS = { OPEN: 'مفتوحة', CLOSED: 'مقفلة' } as const;

const CHECK_LABELS: Record<PeriodCheckItem['code'], string> = {
  BANK_NOT_RECONCILED: 'كل بنك عليه حركة مُطابق حتى نهاية الفترة',
  OPENING_DIFFERENCE_NOT_ZERO: 'رصيد «فروق أرصدة افتتاحية» صفر',
};

const MODE_LABELS = { block: 'يمنع الإقفال', warn: 'تحذير', off: 'غير مفعّل' } as const;

function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('ar-EG', { dateStyle: 'medium', timeStyle: 'short' });
}

/** وصف بند فاشل بالعربية من تفاصيله — رسالة الـ API للمطوّر لا للمستخدم. */
function describe(item: PeriodCheckItem): string {
  if (item.code === 'BANK_NOT_RECONCILED') {
    const last = item.details.lastReconciledDate;
    return (
      '«' +
      (item.details.nameAr ?? '') +
      '» ' +
      (last ? 'آخر مطابقة حتى ' + last : 'لم يُطابق قط') +
      ' — يلزم كشف حتى ' +
      (item.details.periodEndDate ?? '')
    );
  }
  // الرصيد مدين ناقص دائن: السالب رصيد دائن، ويُعرض بقيمته وجهته لا بإشارة
  const balance = Money.of(item.details.balance ?? '0');
  return (
    'الحساب ' +
    (item.details.accountCode ?? '') +
    ' رصيده ' +
    formatMoney(balance.abs()) +
    (balance.isNegative() ? ' دائن' : ' مدين') +
    ' — يُسوّى بقيد قبل الإقفال'
  );
}

function messageOf(caught: unknown): string {
  if (caught instanceof ProblemError) return caught.problem.detail ?? caught.problem.title;
  return caught instanceof Error ? caught.message : String(caught);
}

/**
 * الفترات المالية. الإقفال يمنع أى ترحيل بتاريخ داخلها — والقاعدة تفرضه بنفسها.
 * قبله قائمة فحوص حسب إعدادات الشركة (ADR-0006): ما يمنع يُعرض ولا يُقفل معه،
 * وما يحذّر يحتاج إقرارًا صريحًا. وفى آخر فترة من السنة يظهر قيد إقفال السنة للمالك.
 * إعادة الفتح تمس أرقامًا رآها أحد، فسببها إلزامى ويُحفظ فى سجل التدقيق.
 */
export default function Periods({ companyId, periods, canManage, canReopen, onChanged }: Props) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reopening, setReopening] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [closing, setClosing] = useState<FiscalPeriodDto | null>(null);
  const [checks, setChecks] = useState<PeriodCloseChecksResponse | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  async function run(periodId: string, action: () => Promise<unknown>): Promise<boolean> {
    setBusyId(periodId);
    setError(null);
    setNotice(null);
    try {
      await action();
      setReopening(null);
      setReason('');
      onChanged();
      return true;
    } catch (caught) {
      setError(messageOf(caught));
      return false;
    } finally {
      setBusyId(null);
    }
  }

  async function openChecklist(period: FiscalPeriodDto): Promise<void> {
    setClosing(period);
    setChecks(null);
    setAcknowledged(false);
    setError(null);
    setNotice(null);
    try {
      setChecks(await api.periodCloseChecks(companyId, period.id));
    } catch (caught) {
      setError(messageOf(caught));
    }
  }

  async function confirmClose(period: FiscalPeriodDto): Promise<void> {
    const ok = await run(period.id, () =>
      api.closePeriod(companyId, period.id, crypto.randomUUID(), acknowledged),
    );
    if (ok) {
      setClosing(null);
      setChecks(null);
      setNotice('أُقفلت الفترة ' + period.startDate + ' — ' + period.endDate + '.');
    }
  }

  async function yearEnd(period: FiscalPeriodDto, fiscalYear: number): Promise<void> {
    const ok = window.confirm(
      'ترحيل قيد إقفال السنة المالية ' +
        fiscalYear +
        '؟ تُصفَّر كل حسابات الإيرادات والمصروفات ويُرحَّل صافى النتيجة إلى الأرباح المرحلة. ' +
        'قائمة الدخل للسنة تبقى كما هى، والقيد لا يتكرر.',
    );
    if (!ok) return;
    setBusyId(period.id);
    setError(null);
    try {
      const result = await api.yearEndClose(companyId, period.id, crypto.randomUUID());
      setNotice(
        'رُحّل قيد إقفال ' +
          result.fiscalYear +
          ' (' +
          (result.journal.number ?? '') +
          ') — صافى النتيجة ' +
          formatMoney(result.netIncome) +
          '.',
      );
      setChecks(await api.periodCloseChecks(companyId, period.id));
      onChanged();
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusyId(null);
    }
  }

  function reopen(period: FiscalPeriodDto): void {
    void run(period.id, () =>
      api.reopenPeriod(companyId, period.id, crypto.randomUUID(), reason.trim()),
    );
  }

  const failed = checks ? [...checks.blocking, ...checks.warnings] : [];

  return (
    <div className="panel">
      <h2>الفترات المالية</h2>
      <p className="sub">
        الفترة المقفلة لا تقبل أى قيد بتاريخ داخلها. إعادة الفتح تحتاج سببًا يُحفظ فى سجل التدقيق.
      </p>
      {error && <p className="error">{error}</p>}
      {notice && <p className="sub">{notice}</p>}
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>من</th>
              <th>إلى</th>
              <th>الحالة</th>
              <th>أُقفلت فى</th>
              {canManage && <th aria-label="إجراء" />}
            </tr>
          </thead>
          <tbody>
            {periods.map((period) => (
              <tr key={period.id}>
                <td>{period.startDate}</td>
                <td>{period.endDate}</td>
                <td>
                  <span className={'pill' + (period.status === 'CLOSED' ? ' bad' : '')}>
                    {STATUS_LABELS[period.status]}
                  </span>
                </td>
                <td>{formatDateTime(period.closedAt)}</td>
                {canManage && (
                  <td>
                    {period.status === 'OPEN' ? (
                      <button
                        type="button"
                        className="ghost"
                        disabled={busyId === period.id}
                        onClick={() => void openChecklist(period)}
                      >
                        إقفال…
                      </button>
                    ) : !canReopen ? (
                      <span className="label">إعادة الفتح للمالك</span>
                    ) : reopening === period.id ? (
                      <div className="reopen-form">
                        <input
                          value={reason}
                          maxLength={500}
                          onChange={(event) => setReason(event.target.value)}
                          placeholder="سبب إعادة الفتح (١٠ أحرف على الأقل)"
                          aria-label="سبب إعادة الفتح"
                        />
                        <button
                          type="button"
                          className="ghost"
                          disabled={busyId === period.id || reason.trim().length < 10}
                          onClick={() => reopen(period)}
                        >
                          تأكيد الفتح
                        </button>
                        <button type="button" className="link" onClick={() => setReopening(null)}>
                          إلغاء
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => {
                          setReopening(period.id);
                          setReason('');
                        }}
                      >
                        إعادة فتح
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {periods.length === 0 && (
              <tr>
                <td colSpan={canManage ? 5 : 4} className="sub">
                  لا توجد فترات مالية لهذه الشركة.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {closing && (
        <section className="close-checks" aria-label="قائمة ما قبل الإقفال">
          <h3>
            قبل إقفال {closing.startDate} — {closing.endDate}
          </h3>
          {!checks ? (
            <p className="sub">جارٍ الفحص…</p>
          ) : (
            <>
              {checks.checks.length === 0 ? (
                <p className="sub">لا فحوص مفعّلة فى إعدادات الشركة.</p>
              ) : (
                <ul>
                  {checks.checks.map((check) => (
                    <li key={check.code}>
                      <span
                        className={
                          'pill' + (check.passed ? '' : check.mode === 'block' ? ' bad' : ' warn')
                        }
                      >
                        {check.passed ? 'سليم' : MODE_LABELS[check.mode]}
                      </span>
                      {CHECK_LABELS[check.code]}
                    </li>
                  ))}
                </ul>
              )}
              {failed.length > 0 && (
                <ul>
                  {failed.map((item, index) => (
                    <li key={item.code + index} className="sub">
                      {describe(item)}
                    </li>
                  ))}
                </ul>
              )}

              {checks.yearEnd.isLastPeriodOfYear && (
                <div className="close-checks">
                  <h3>إقفال السنة المالية {checks.yearEnd.fiscalYear}</h3>
                  {checks.yearEnd.mode === 'auto_entry' ? (
                    checks.yearEnd.journalId ? (
                      <p className="sub">
                        قيد الإقفال مُرحَّل ({checks.yearEnd.journalNumber}) — النتيجة فى الأرباح
                        المرحلة.
                      </p>
                    ) : canReopen ? (
                      <div className="panel-actions">
                        <p className="sub">
                          قيد الإقفال يصفّر الإيرادات والمصروفات إلى الأرباح المرحلة. يُرحَّل قبل
                          إقفال الفترة.
                        </p>
                        <button
                          type="button"
                          className="ghost"
                          disabled={busyId === closing.id}
                          onClick={() => void yearEnd(closing, checks.yearEnd.fiscalYear)}
                        >
                          ترحيل قيد إقفال السنة
                        </button>
                      </div>
                    ) : (
                      <p className="sub">قيد إقفال السنة يرحّله المالك.</p>
                    )
                  ) : (
                    <p className="sub">
                      {checks.yearEnd.mode === 'manual'
                        ? 'الإعدادات: إقفال السنة بقيد يدوى يكتبه المحاسب.'
                        : 'الإعدادات: النتيجة تُعرض فى التقارير دون قيد إقفال.'}
                    </p>
                  )}
                </div>
              )}

              {checks.warnings.length > 0 && checks.canClose && (
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(event) => setAcknowledged(event.target.checked)}
                  />
                  اطّلعت على التحذيرات وأريد الإقفال رغمها
                </label>
              )}
              <div className="panel-actions">
                <button
                  type="button"
                  disabled={
                    busyId === closing.id ||
                    !checks.canClose ||
                    (checks.warnings.length > 0 && !acknowledged)
                  }
                  onClick={() => void confirmClose(closing)}
                >
                  تأكيد الإقفال
                </button>
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    setClosing(null);
                    setChecks(null);
                  }}
                >
                  إلغاء
                </button>
              </div>
              {!checks.canClose && (
                <p className="error">لا يمكن الإقفال حتى تُحل البنود الممنوعة أعلاه.</p>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
